import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Badge, Button, Dropdown, Popover, Tooltip } from 'antd';
import {
	ApiOutlined,
	BellOutlined,
	DashboardOutlined,
	FieldTimeOutlined,
	LogoutOutlined,
	SettingOutlined,
	FileTextOutlined,
	UserOutlined,
	ReadOutlined,
	QuestionCircleOutlined,
	SendOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
	ADMIN_BADGE,
	BOOK,
	BELL,
	CARD_ICON,
	CHEVRON_LEFT,
	CHEVRON_RIGHT,
	CLOSE,
	COLLECTION_ICON,
	COMPASS,
	HISTORY,
	HOME,
	MAGNIFYING_GLASS,
	PERSON,
} from './components/icons';
import CommandPalette from './components/common/CommandPalette';
import ShortcutsModal from './components/common/ShortcutsModal';
import PreferencesModal from './components/preferences/PreferencesModal';
import NotificationsDrawer from './components/notifications/NotificationsDrawer';
import UserAvatar from './components/users/UserAvatar';
import UnverifiedBanner from './components/users/UnverifiedBanner';
import EmailDeliveryBanner from './components/users/EmailDeliveryBanner';
import RecordBadge from './components/common/RecordBadge';
import RailPinned from './components/common/RailPinned';
import ActiveTagChip, { ACTIVE_TAG_MENU_KEYS, activeTagMenuItems } from './components/tags/ActiveTagChip';
import AddItemButton from './components/items/AddItemButton';
import { BrandLockup, BrandMark } from './components/brand/Brand';
import { userLabel } from './components/users/UserChip';
import useCollectionIdentity from './hooks/useCollectionIdentity';
import { useCollections } from './contexts/CollectionsContext';
import { collectionScopeFromPath } from './utils/collectionScope';
import useRecentCollections from './hooks/useRecentCollections';
import { parentKicker } from './utils/collectionFamily';
import useApiVersion from './hooks/useApiVersion';
import usePageStats from './hooks/usePageStats';
import { pageStatsPopoverProps, usePageStatsDismiss } from './components/common/PageStatsPopover';
import { versionLine, webVersion } from './config/buildInfo';
import { copyTextToClipboard } from './utils/clipboard';
import useLauncherPaletteActions from './hooks/useLauncherPaletteActions';
import { useHomeTags } from './hooks/useHome';
import useActiveTag from './hooks/useActiveTag';
import { activeTagRefusal } from './utils/activeTag';
import { tagPagePath } from './utils/tags';
import { useCurrentUser } from './contexts/CurrentUserContext';
import { useWelcomeTour } from './contexts/WelcomeTourContext';
import { tourTargetProps } from './tours/tourTarget';
import { Skeleton } from './components/loading';
import { useOptionalFavorites } from './contexts/FavoritesContext';
import { useInboxBadge } from './contexts/NotificationsContext';
import { migrateLocalDrafts } from './services/drafts';
import { hasRole, isAdmin, isTokenValid } from './utils';
import { ADMIN_WINDOW_NAME, adminPortalPath, openAdminPortal } from './utils/adminPortal';
import useWindowClass from './hooks/useWindowClass';
import useRailOverlay from './hooks/useRailOverlay';

const COLLAPSE_KEY = 'keepr.railCollapsed';

const NAV_ITEMS = [
	{ key: 'home', label: 'Home', path: '/', icon: HOME, exact: true },
	{ key: 'collections', label: 'Collections', path: '/collections', icon: COLLECTION_ICON },
	{ key: 'cards', label: 'Cards', path: '/cards', icon: CARD_ICON },
	// The rail only (plan ruling e09): the phone's tab bar is for the sections
	// people live in, so there Discover is a door in the Collections bar and
	// the Me menu instead of a sixth tab.
	{ key: 'discover', label: 'Discover', path: '/discover', icon: COMPASS, desktopOnly: true },
];

/**
 * The platform shell: a 216px rail (wordmark, ⌘K search, product nav,
 * pinned collections, recently viewed, user foot) beside the work area. Routes render
 * their own context bars. In a compact window (useWindowClass, under 600) the rail
 * becomes a bottom tab bar.
 *
 * Admin is one link — the portal is its own UI in its own browser tab
 * (KPR-309, components/admin/AdminLayout.js), outside this shell.
 */
export default function AppLayout() {
	const [storedCollapsed, setCollapsed] = useState(() => {
		try { return window.localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
	});
	// The welcome tour points at the sidebar's rows, which a collapsed sidebar
	// shows as bare icons: while it runs, the sidebar is held open. Only what
	// is DRAWN changes; the person's own choice (above, and in storage) is
	// never rewritten, so the tour ending, however it ends, puts it back.
	const welcomeTour = useWelcomeTour();
	const [prefsOpen, setPrefsOpen] = useState(false);
	const [inboxOpen, setInboxOpen] = useState(false);
	const [paletteOpen, setPaletteOpen] = useState(false);
	// The palette's "Add item to {personal collection}" opens this dialog.
	const [personalAddOpen, setPersonalAddOpen] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const { message } = App.useApp();
	// The account menu's footer: which web build this is (inlined at build
	// time) and which API build answered — asked of /health when the menu
	// first opens, never on mount.
	const apiVersion = useApiVersion();
	const onProfileMenuOpen = open => { if (open) apiVersion.load(); };
	// Page stats (KPR-543): this page's timings, read from the browser when the
	// account menu's entry is pressed. Nothing is sent or stored.
	const readPageStats = usePageStats();
	const [pageStats, setPageStats] = useState({ open: false, stats: null });
	const closePageStats = useCallback(() => setPageStats(prev => (prev.open ? { ...prev, open: false } : prev)), []);
	usePageStatsDismiss(pageStats.open, closePageStats);
	// The signed-in user's record (null while logged out) — drives the rail
	// foot and refreshes when the profile page edits it.
	const { user: currentUser, personalCollectionId, loading: userLoading } = useCurrentUser();
	// Optional: the shell renders in tests (and before the providers mount)
	// without an inbox, which simply means "nothing unread". Only the badge
	// slice: the rows and the writers are the drawer's, and a poll that moved
	// nothing must not re-render the shell (KPR-1549).
	const inbox = useInboxBadge();
	const unreadCount = inbox?.unreadCount || 0;
	// A count is shown only once it is known: until the FIRST read lands the
	// slot holds a placeholder, never 0 (platform-standards §14). `loading`
	// does not go up again for a poll, so a known count never flickers.
	const unreadKnown = inbox ? !inbox.loading : true;
	const favorites = useOptionalFavorites();

	// The compact class (under 600, platform-standards §18) draws the phone's
	// chrome: the mobile bar and the bottom tab bar, and so does a short window
	// (under 500 tall: a phone held sideways), at any width. Otherwise the rail:
	// medium folds it to 56px and opens it as an overlay on demand; expanded
	// draws the person's own choice (`keepr.railCollapsed`), which rules there
	// and only there.
	const { compact, medium, short } = useWindowClass();
	const isMobile = compact || short;
	const foldedRail = medium && !isMobile;
	const { overlayOpen, openRail, closeRail, railRef, brandButtonRef, onRailClick } = useRailOverlay({
		foldedRail,
		disabled: isMobile,
		pathname: location.pathname,
		onToggleFold: () => toggleCollapsed(),
	});
	// The tour points at rail rows, which a folded rail draws as bare icons:
	// while it runs, a medium window draws the rail open, over the page and
	// without a scrim (the tour has its own), and expanded holds it unfolded.
	// Only what is DRAWN changes; the person's choice is never rewritten.
	const tourHolds = Boolean(welcomeTour.railForcedOpen);
	const railOpenOverlay = foldedRail && (overlayOpen || tourHolds);
	const collapsed = foldedRail ? !railOpenOverlay : storedCollapsed && !tourHolds;

	const toggleCollapsed = () => {
		setCollapsed(prev => {
			const next = !prev;
			try { window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* convenience */ }
			return next;
		});
	};

	// Drafts used to live in this browser's storage; the first load of a
	// signed-in session moves the ones typed during it to the server (and
	// drops any it cannot vouch for — services/drafts.js). Once per token.
	const signedInUserId = currentUser?._id;
	useEffect(() => {
		if (!signedInUserId || !isTokenValid()) return;
		migrateLocalDrafts().catch(() => { /* kept locally; tried again next load */ });
	}, [signedInUserId]);

	// ⌘K / Ctrl+K opens the palette from anywhere.
	useEffect(() => {
		const handler = (event) => {
			if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
				event.preventDefault();
				setPaletteOpen(open => !open);
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// Pinned section: the personal collection first, then favorited
	// collections in the person's own order (reordered on Home or the
	// collections page — the rail only lists). The rows come from the shared
	// collections list (contexts/CollectionsContext — one fetch a session,
	// which also teaches the qualified badge's resolver); the rail only needs
	// ids, names and identity.
	const favoriteIds = favorites?.favoriteCollectionIds;
	const { collectionsById, loading: collectionsLoading } = useCollections();
	const wantPinned = Boolean((favoriteIds && favoriteIds.size) || personalCollectionId) && isTokenValid();
	// A failed read settles too: the rail works without the pinned section.
	const pinnedFetched = !collectionsLoading;

	// The personal collection is always the first pin (it cannot be starred,
	// and it cannot be unpinned); favorites follow in the person's order
	// (userFavorites `position`, what the context lists them in).
	const personal = personalCollectionId ? collectionsById[personalCollectionId] || null : null;
	const favoriteOrder = favorites?.favoriteCollectionOrder;
	const pinnedFavorites = useMemo(() => (favoriteOrder || [...(favoriteIds || [])])
		.filter(id => id !== personalCollectionId)
		.map(id => collectionsById[id])
		.filter(Boolean), [favoriteOrder, favoriteIds, collectionsById, personalCollectionId]);

	// Pinned tags (tags T5): a tag page's Pin puts it in the rail too. Their
	// names come from Home's resolved read (GET /api/home `tags`, re-checked
	// under the person's access now), asked only when the favorites say there
	// is a pinned tag at all — and again whenever a pin changes (useHome
	// follows FavoritesContext.pinsVersion).
	const hasPinnedTags = Boolean(favorites?.pinnedTagIds?.length) && isTokenValid();
	const railTags = useHomeTags({ enabled: hasPinnedTags });
	const pinnedTags = hasPinnedTags && railTags ? railTags : [];
	// The Pinned section's rows are unknown until the favorites, the person's
	// own record (the personal collection) and the collections read have
	// answered; a skeleton holds the place so the rail does not jump.
	// A refresh keeps the rows it has (stale-while-revalidate): the placeholder
	// is for when there is nothing to show yet.
	const pinnedLoading = isTokenValid() && Boolean(currentUser || userLoading)
		&& !personal && !pinnedFavorites.length && !pinnedTags.length
		&& (Boolean(userLoading) || Boolean(favorites?.loading) || (wantPinned && !pinnedFetched));

	// The palette's first row: add an item to the personal collection from
	// anywhere — the thing a person with a thought and no collection for it
	// wants most. Offered once the collection's name is known. After it, the
	// Home launchers and favorite cards from every collection (read once the
	// palette opens).
	const launcherActions = useLauncherPaletteActions({ enabled: paletteOpen });
	// Inside a collection the palette offers "Search in <collection>" for the
	// typed term (KPR-356). The name must be known before the row is offered.
	const scopeId = collectionScopeFromPath(location.pathname);
	const scopeIdentity = useCollectionIdentity(scopeId);
	const collectionScope = scopeId && scopeIdentity?.name ? { id: scopeId, name: scopeIdentity.name } : null;
	const paletteActions = useMemo(() => [
		...(personal ? [{
			key: 'add-item-personal',
			title: `Add item to ${personal.name || 'your personal collection'}`,
			run: () => setPersonalAddOpen(true),
		}] : []),
		...launcherActions,
	], [personal, launcherActions]);

	// Recent: the collections this user opened, newest first. A pinned
	// collection still appears here when it was just opened — the two sections
	// answer different questions (what I keep, where I have been), and hiding
	// the overlap would leave Recent empty for anyone who pins what they use.
	const { rows: recent, loading: recentLoading, forget: forgetRecent } = useRecentCollections({ limit: 5 });

	const isNavActive = (item) => {
		if (item.exact) return location.pathname === item.path;
		return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
	};

	// The active tag (docs/plans/tags-active-tag-proposal.md, b49): the rail's
	// foot shows it (ActiveTagChip); a phone has no rail, so its Me menu leads
	// with the same chip and Stop adding it.
	const { activeTag, stop: stopActiveTag } = useActiveTag();
	const phoneActiveTagItems = isMobile ? activeTagMenuItems(activeTag, { closeIcon: <CLOSE /> }) : [];
	const stopAddingActiveTag = async () => {
		const name = activeTag?.tag?.name || 'this tag';
		try {
			await stopActiveTag();
			message.success(`Stopped adding “${name}”.`);
		} catch (error) {
			message.error(activeTagRefusal(error));
		}
	};

	const copyVersions = async () => {
		const line = versionLine(webVersion, apiVersion.version);
		if (await copyTextToClipboard(line)) message.success('Versions copied');
		else message.error('Could not copy the versions');
	};

	// Who gets a way into the admin portal: admins, and reviewers (who see
	// Review alone). The portal's own guard decides again; this only hides a
	// link that would end in a 403.
	const adminEntry = isAdmin() || hasRole('reviewer');
	const adminPath = adminPortalPath({ admin: isAdmin() });

	const profileMenu = {
		// The touch floor on a phone hangs off this class (shell.css).
		className: 'account-menu',
		items: [
			...phoneActiveTagItems,
			// Who is signed in, so a person can verify the account before they
			// act. A caption, not an action: it does nothing when clicked.
			...(currentUser?.email ? [{
				key: 'account-email',
				className: 'account-menu__email',
				title: currentUser.email,
				label: <span className="account-menu__email-text">{currentUser.email}</span>,
			}, { type: 'divider' }] : []),
			{ key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
			// The five-minute path from "I have an account" to "my assistant
			// just read my collection" starts here, not in the keys table.
			{ key: 'connect', icon: <ApiOutlined />, label: 'Connect an assistant' },
			// The phone's way to Discover (no rail there, no sixth tab).
			{ key: 'discover', icon: <COMPASS />, label: 'Discover' },
			// Publishing (keepr-api KPR-1331): only for an account a third-party
			// publisher names — user-info says which. The rail and the phone's
			// tab bar share this menu, so it is one entry for both.
			...(currentUser?.publisher ? [{ key: 'publishing', icon: <SendOutlined />, label: 'Publishing' }] : []),
			// New items started and left, across every collection.
			{ key: 'drafts', icon: <FileTextOutlined />, label: 'Drafts' },
			{ key: 'preferences', icon: <SettingOutlined />, label: 'Preferences' },
			{
				key: 'notifications',
				// The count rides the bell itself so the menu says how many even
				// once it is open (the foot dot only says "something").
				icon: <Badge count={unreadCount} size='small'><BellOutlined /></Badge>,
				label: 'Notifications',
			},
			// The inbox is the drawer above; this is the list of what can
			// reach you at all, and the three delivery masters over it.
			{ key: 'notification-settings', icon: <SettingOutlined />, label: 'Notification settings' },
			// The rail's Docs link does not exist on a phone (the rail unmounts
			// below md); this menu is the tab bar's Account menu too, so it is
			// the phone's way in.
			{ key: 'docs', icon: <ReadOutlined />, label: 'Documentation' },
			// Whether keepr is up, which version of each part is running and
			// what changed (KPR-1178). The public footer's link is the only
			// other door, and the app shell never shows that footer. Same tab:
			// /status is an in-app page whose header says Open keepr.
			{ key: 'status', icon: <DashboardOutlined />, label: 'Status' },
			// How long this page took (KPR-543): worked out in the browser, shown
			// in a small popover, sent and stored nowhere.
			{ key: 'page-stats', icon: <FieldTimeOutlined />, label: 'Page stats' },
			// There is no Help menu; this is where the welcome tour is replayed
			// (KPR-613). Always offered, whatever the saved state: the tour is
			// the person's to take again.
			{ key: 'tour', icon: <QuestionCircleOutlined />, label: 'Take the tour' },
			// The phone's way into the admin portal (KPR-146): no rail there.
			// It opens in its own tab, as the rail's link does on a computer.
			...(isMobile && adminEntry ? [{ key: 'admin', icon: <ADMIN_BADGE />, label: 'Admin' }] : []),
			{ type: 'divider' },
			{ key: 'logout', icon: <LogoutOutlined />, label: 'Sign out' },
			// A quiet footer, not an action: muted, small, tabular, no icon —
			// the line a person reads out when something is wrong, and copies
			// with one click. "API unknown" for an API older than release
			// identity (its /health says only { ok: true }) or one that did
			// not answer; "API …" while the first open is still asking.
			{ type: 'divider' },
			{
				key: 'build-version',
				className: 'account-menu__version',
				title: 'Copy the web and API versions',
				label: (
					<span className="account-menu__version-text" aria-live="polite">
						{apiVersion.status === 'loading' && !apiVersion.version ? (
							<>
								{`Web ${webVersion} · API `}
								<span className="account-menu__version-pending" aria-label="loading"><Skeleton variant="line" width={28} /></span>
							</>
						) : versionLine(webVersion, apiVersion.version)}
					</span>
				),
			},
		],
		onClick: ({ key }) => {
			if (key === 'logout') navigate('/logout');
			else if (key === 'profile') navigate('/profile');
			else if (key === 'connect') navigate('/account/connect');
			else if (key === 'discover') navigate('/discover');
			else if (key === 'publishing') navigate('/publishing');
			else if (key === 'drafts') navigate('/drafts');
			else if (key === 'preferences') setPrefsOpen(true);
			else if (key === 'notifications') setInboxOpen(true);
			else if (key === 'notification-settings') navigate('/account/notifications');
			else if (key === 'docs') navigate('/docs');
			else if (key === 'status') navigate('/status');
			else if (key === 'page-stats') setPageStats({ open: true, stats: readPageStats() });
			else if (key === 'tour') welcomeTour.start();
			else if (key === 'admin') openAdminPortal(adminPath);
			else if (key === 'build-version') copyVersions();
			else if (key === ACTIVE_TAG_MENU_KEYS.open) { const path = tagPagePath(activeTag?.tag); if (path) navigate(path); }
			else if (key === ACTIVE_TAG_MENU_KEYS.stop) stopAddingActiveTag();
		},
	};

	const railNav = (
		<nav className="rail__nav" aria-label="Product">
			{NAV_ITEMS.map(item => {
				const Icon = item.icon;
				const active = isNavActive(item);
				return (
					<Link
						key={item.key}
						to={item.path}
						className={`rail__nav-row${active ? ' rail__nav-row--active' : ''}`}
						aria-current={active ? 'page' : undefined}
						title={collapsed ? item.label : undefined}
						{...tourTargetProps(`nav-${item.key}`)}
					>
						<Icon className="rail__nav-icon" aria-hidden="true" />
						{!collapsed && <span>{item.label}</span>}
					</Link>
				);
			})}
			{currentUser ? (
				// Notifications live on a bell in the rail with a count — never
				// as an unexplained dot on the avatar.
				<button
					type="button"
					className="rail__nav-row rail__nav-row--button"
					onClick={() => setInboxOpen(true)}
					title={collapsed ? `Notifications${unreadCount ? ` (${unreadCount})` : ''}` : undefined}
					aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
					{...tourTargetProps('notifications')}
				>
					{collapsed ? (
						<Badge count={unreadCount} size="small" offset={[4, -2]}>
							<BELL className="rail__nav-icon" aria-hidden="true" />
						</Badge>
					) : (
						<BELL className="rail__nav-icon" aria-hidden="true" />
					)}
					{!collapsed && <span>Notifications</span>}
					{!collapsed && !unreadKnown ? (
						<span className="rail__nav-count-pending" aria-hidden="true"><Skeleton variant="line" width={18} /></span>
					) : null}
					{!collapsed && unreadKnown && unreadCount > 0 ? (
						<span className="rail__nav-count" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
					) : null}
				</button>
			) : userLoading ? (
				// The bell row arrives with the person; hold its place.
				<div className="rail__nav-row rail__nav-row--pending" aria-hidden="true">
					<Skeleton variant="avatar" size={18} />
					{!collapsed && <Skeleton variant="line" width={84} />}
				</div>
			) : null}
			{/* Beside Admin, and above it because everyone gets this one: the
			    guides are for everyone, signed in or not; the portal's
			    internals tabs are admin-gated inside it. */}
			<Link
				to="/docs"
				className={`rail__nav-row${location.pathname.startsWith('/docs') ? ' rail__nav-row--active' : ''}`}
				aria-current={location.pathname.startsWith('/docs') ? 'page' : undefined}
				title={collapsed ? 'Docs' : undefined}
			>
				<BOOK className="rail__nav-icon" aria-hidden="true" />
				{!collapsed && <span>Docs</span>}
			</Link>
			{/* The admin portal is its own UI in its own tab (KPR-309):
			    a plain anchor to the named window, so it never shows as the
			    current page here. A reviewer's way in too: the portal opens
			    on Review, the one section their role reaches
			    (docs/plans/personal-discover-global.md §4.2). */}
			{adminEntry ? (
				<a
					href={adminPath}
					target={ADMIN_WINDOW_NAME}
					onClick={event => openAdminPortal(adminPath, event)}
					className="rail__nav-row"
					title={collapsed ? 'Admin (opens in its own tab)' : 'Opens in its own tab'}
					aria-label="Admin, opens in its own tab"
				>
					<ADMIN_BADGE className="rail__nav-icon" aria-hidden="true" />
					{!collapsed && <span>Admin</span>}
				</a>
			) : null}
		</nav>
	);

	const userFoot = currentUser ? (
		<div className="rail__foot">
			<ActiveTagChip collapsed={collapsed} />
			<Dropdown menu={profileMenu} trigger={['click']} placement="topLeft" onOpenChange={onProfileMenuOpen}>
				<Popover {...pageStatsPopoverProps({ open: pageStats.open && !isMobile, stats: pageStats.stats, placement: 'topLeft' })}>
					<button type="button" className="rail__user" aria-label="Account menu" {...tourTargetProps('account-menu')}>
						<UserAvatar user={currentUser} size={26} />
						{/* The display name; the email is the first line of the menu. */}
						{!collapsed && (
							<span className="rail__user-name">
								{userLabel(currentUser)}
							</span>
						)}
					</button>
				</Popover>
			</Dropdown>
		</div>
	) : userLoading ? (
		// A token is present and the person's record is on its way: neutral,
		// never the signed-out Sign in (platform-standards §14, no wrong-state
		// flash). Same footprint as the user row below.
		<div className="rail__foot" aria-busy="true">
			<div className="rail__user rail__user--pending" data-testid="rail-user-pending">
				<Skeleton variant="avatar" size={26} />
				{!collapsed && <span className="rail__user-name"><Skeleton variant="line" width={72} /></span>}
			</div>
		</div>
	) : (
		<div className="rail__foot">
			<Link to="/login" className="rail__user rail__user--anon" aria-label="Sign in">
				<PERSON aria-hidden="true" />
				{!collapsed && <span className="rail__user-name">Sign in</span>}
			</Link>
		</div>
	);

	return (
		<div className={`shell${isMobile ? ' shell--mobile' : ''}`}>
			{!isMobile && railOpenOverlay && <div className="rail-spacer" aria-hidden="true" />}
			{!isMobile && (
				<aside
					ref={railRef}
					className={`rail${collapsed ? ' rail--collapsed' : ''}${foldedRail ? ' rail--medium' : ''}${railOpenOverlay ? ' rail--overlay' : ''}`}
					role={overlayOpen ? 'dialog' : undefined}
					aria-modal={overlayOpen ? 'true' : undefined}
					aria-label={overlayOpen ? 'Navigation' : undefined}
					onClick={onRailClick}
				>
					<div className="rail__top">
						{/* Mark left of the ink wordmark; collapsed, the mark alone.
						    A folded medium rail has no room to be a link home: the
						    mark opens the rail over the page instead (§18). */}
						{foldedRail && !railOpenOverlay ? (
							<button
								type="button"
								ref={brandButtonRef}
								className="rail__brand rail__brand--button"
								aria-label="Open navigation"
								aria-expanded="false"
								title="Open navigation (⌘\)"
								onClick={openRail}
							>
								<BrandMark size={20} />
							</button>
						) : (
							<Link to="/" className="rail__brand" aria-label="keepr home">
								{collapsed ? <BrandMark size={20} /> : <BrandLockup />}
							</Link>
						)}
						{foldedRail ? (
							overlayOpen ? (
								<Button
									type="text"
									size="small"
									className="rail__collapse"
									icon={<CHEVRON_LEFT />}
									onClick={() => closeRail()}
									aria-label="Close navigation"
								/>
							) : null
						) : (
							<Tooltip title={collapsed ? 'Expand navigation' : 'Collapse navigation'} placement="right">
								<Button
									type="text"
									size="small"
									className="rail__collapse"
									icon={collapsed ? <CHEVRON_RIGHT /> : <CHEVRON_LEFT />}
									onClick={toggleCollapsed}
									aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
								/>
							</Tooltip>
						)}
					</div>

					<button
						type="button"
						className="rail__search"
						onClick={() => setPaletteOpen(true)}
						title={collapsed ? 'Search (⌘K)' : undefined}
						{...tourTargetProps('search')}
					>
						<MAGNIFYING_GLASS aria-hidden="true" />
						{!collapsed && (
							<>
								<span className="rail__search-label">Search</span>
								<kbd className="rail__search-kbd">⌘K</kbd>
							</>
						)}
					</button>

					{railNav}

					{/* Only the lists scroll: the account foot below stays pinned to the
					    bottom-left of the viewport however long Pinned and Recent get. */}
					<div className="rail__scroll" {...tourTargetProps('rail-lists')}>
						<RailPinned
							personal={personal}
							collections={pinnedFavorites}
							tags={pinnedTags}
							loading={pinnedLoading}
							collapsed={collapsed}
							pathname={location.pathname}
						/>

						{currentUser || userLoading ? (
							<div className="rail__recent">
								{!collapsed && <div className="micro-label rail__section-label">Recent</div>}
								{recentLoading ? (
									<div className="rail__recent-pending" aria-hidden="true" data-testid="rail-recent-pending">
										<Skeleton variant="line" count={2} />
									</div>
								) : null}
								{recent.map(({ id, collection }) => {
									const path = `/collections/${id}/items`;
									const active = location.pathname.startsWith(`/collections/${id}`);
									// Same row as a pin, plus an X that forgets the visit.
									// The X only exists in the expanded rail — collapsed
									// there is no room, and the History page can forget it.
									return (
										<div key={id} className="rail__recent-row">
											<RecordBadge
												kind="collection"
												record={collection}
												variant="plain"
												size="xs"
												tileSize={21}
												kicker={collapsed ? undefined : (parentKicker(collection) || undefined)}
												title={collapsed ? null : collection?.name}
												tooltip={collapsed ? [parentKicker(collection), collection?.name].filter(Boolean).join(' · ') : undefined}
												aria-label={collection?.name}
												to={path}
												block
												className={`rail__pin${active ? ' rail__pin--active' : ''}`}
											/>
											{!collapsed && (
												<button
													type="button"
													className="rail__recent-forget"
													onClick={() => forgetRecent(id)}
													aria-label={`Remove ${collection?.name || 'collection'} from recent`}
													title="Remove from recent"
												>
													<CLOSE aria-hidden="true" />
												</button>
											)}
										</div>
									);
								})}
								<Link
									to="/history"
									className={`rail__nav-row rail__recent-all${location.pathname === '/history' ? ' rail__nav-row--active' : ''}`}
									aria-current={location.pathname === '/history' ? 'page' : undefined}
									title={collapsed ? 'View history' : undefined}
								>
									<HISTORY className="rail__nav-icon" aria-hidden="true" />
									{!collapsed && <span>View history</span>}
								</Link>
							</div>
						) : null}
					</div>

					{userFoot}
				</aside>
			)}
			{!isMobile && overlayOpen && (
				<div className="rail-scrim" data-testid="rail-scrim" onClick={() => closeRail()} aria-hidden="true" />
			)}

			<div className="shell__main">
				{isMobile && (
					/* The mobile header bar. Its one job today is the bell: below
					   md the rail unmounts and the bottom tabs navigate between
					   SECTIONS, while the inbox is a drawer over wherever you
					   already are — so the bell belongs up here, not down there,
					   and until this bar existed the unread count was invisible
					   on a phone unless you opened the account menu.

					   It scrolls with the page rather than sticking: the sticky
					   context bar is pinned at top: 0 and the items table
					   measures ITS height as the offset for its own sticky
					   header (ItemsTable.measureContextBar), so a second sticky
					   bar above it would slide that header underneath. */
					<header className="mobile-bar">
						<Link to="/" className="mobile-bar__brand" aria-label="keepr home">
							<BrandLockup />
						</Link>
						{currentUser ? (
							<Badge dot={unreadCount > 0} className="mobile-bar__badge" offset={[-3, 3]}>
								<Button
									type="text"
									className="mobile-bar__bell"
									icon={<BELL aria-hidden="true" />}
									onClick={() => setInboxOpen(true)}
									aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
									{...tourTargetProps('notifications')}
								/>
							</Badge>
						) : null}
					</header>
				)}
				<main className="shell__work">
					<UnverifiedBanner />
					<EmailDeliveryBanner />
					<Outlet />
				</main>
			</div>

			{isMobile && (
				<nav className="tab-bar" aria-label="Product">
					{NAV_ITEMS.filter(item => !item.desktopOnly).map(item => {
						const Icon = item.icon;
						const active = isNavActive(item);
						return (
							<Link
								key={item.key}
								to={item.path}
								className={`tab-bar__tab${active ? ' tab-bar__tab--active' : ''}`}
								{...tourTargetProps(`nav-${item.key}`)}
								aria-current={active ? 'page' : undefined}
							>
								<Icon aria-hidden="true" />
								<span>{item.label}</span>
							</Link>
						);
					})}
					<button type="button" className="tab-bar__tab" onClick={() => setPaletteOpen(true)} {...tourTargetProps('search')}>
						<MAGNIFYING_GLASS aria-hidden="true" />
						<span>Search</span>
					</button>
					{currentUser ? (
						<Dropdown menu={profileMenu} trigger={['click']} placement="topRight" onOpenChange={onProfileMenuOpen}>
							<Popover {...pageStatsPopoverProps({ open: pageStats.open && isMobile, stats: pageStats.stats, placement: 'topRight' })}>
								<button type="button" className="tab-bar__tab" aria-label="Account menu" {...tourTargetProps('account-menu')}>
									<UserAvatar user={currentUser} size={20} />
									<span>Me</span>
								</button>
							</Popover>
						</Dropdown>
					) : userLoading ? (
						// Auth is not known yet: the Me slot as a placeholder, never Sign in.
						<span className="tab-bar__tab tab-bar__tab--pending" aria-busy="true" data-testid="tab-me-pending">
							<Skeleton variant="avatar" size={20} />
							<Skeleton variant="line" width={18} />
						</span>
					) : (
						<Link to="/login" className="tab-bar__tab">
							<PERSON aria-hidden="true" />
							<span>Sign in</span>
						</Link>
					)}
				</nav>
			)}

			<CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} actions={paletteActions} collectionScope={collectionScope} />
			{personalCollectionId ? (
				<AddItemButton
					collectionId={personalCollectionId}
					collection={personal}
					hideTrigger
					open={personalAddOpen}
					onOpenChange={setPersonalAddOpen}
				/>
			) : null}
			{/* One modal for the whole app; every page's ⋯ → Help opens THIS. */}
			<ShortcutsModal />
			<PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
			<NotificationsDrawer open={inboxOpen} onClose={() => setInboxOpen(false)} />
		</div>
	);
}
